/**
 * Das Ergebnis eines Probelaufs — als Antwort auf die Frage „Was passiert mit meinen
 * Artikeln?", nicht auf die Frage „Wie viele API-Calls kostet das?".
 *
 * Oben die Summen als Sätze, darunter ein Artikel je Zeile mit seiner Aktion und, wo es
 * eine gibt, dem Grund. Die Technik (Call-Zahlen, JSON-Payloads) ist da, aber
 * zugeklappt — wer sie braucht, findet sie; wer nicht, sieht sie nicht.
 */
import { useState } from 'react';
import { AlertTriangle, CheckCircle2, MinusCircle, SkipForward } from 'lucide-react';
import { zahl } from '../lib/format';
import type { PlanAktion, ProbelaufAntwort } from '../lib/typen';
import KategorieWahl from './KategorieWahl';

const AKTION: Record<PlanAktion, { text: string; farbe: string; icon: typeof CheckCircle2 }> = {
  anlegen:            { text: 'wird angelegt',        farbe: 'text-green-800 bg-green-50',  icon: CheckCircle2 },
  aktualisieren:      { text: 'wird aktualisiert',    farbe: 'text-green-800 bg-green-50',  icon: CheckCircle2 },
  'nicht-anlegbar':   { text: 'nicht anlegbar',       farbe: 'text-red-900 bg-red-50',      icon: AlertTriangle },
  existiert:          { text: 'existiert schon',      farbe: 'text-[#1A1A1A]/70 bg-[#F7F7F5]', icon: SkipForward },
  'nicht-im-bestand': { text: 'nicht im Bestand',     farbe: 'text-[#1A1A1A]/70 bg-[#F7F7F5]', icon: SkipForward },
  abgewaehlt:         { text: 'abgewählt',            farbe: 'text-[#1A1A1A]/50 bg-white',  icon: MinusCircle },
};

type Filter = 'alle' | 'aktiv' | 'problem' | 'uebersprungen';

export default function ProbelaufPlan({
  ergebnis, entwurfId, aufKategorieGesetzt,
}: {
  ergebnis: ProbelaufAntwort;
  /** Fehlt sie, bleibt die Kategorie-Zuweisung aus (z.B. in reinen Ansichten). */
  entwurfId?: number;
  aufKategorieGesetzt?: (meldung: string) => void;
}) {
  /** Welcher Artikel gerade eine Kategorie bekommt — null = keiner. */
  const [kategorieFuer, setKategorieFuer] = useState<string | null>(null);
  const { plan, aufwand } = ergebnis;
  const s = plan.summen;
  const neuanlage = plan.betriebsart === 'neuanlage';
  const [filter, setFilter] = useState<Filter>('alle');

  const sichtbar = plan.artikel.filter(a => {
    if (filter === 'alle') return true;
    if (filter === 'aktiv') return a.aktion === 'anlegen' || a.aktion === 'aktualisieren';
    if (filter === 'problem') return a.aktion === 'nicht-anlegbar' || (a.ohneBild || a.ohneKategorie) && (a.aktion === 'anlegen' || a.aktion === 'aktualisieren');
    return a.aktion === 'existiert' || a.aktion === 'nicht-im-bestand' || a.aktion === 'abgewaehlt';
  });

  return (
    <div className="space-y-4">
      {/* ── Der Satz, der alles sagt ─────────────────────────────────────── */}
      <div className={`rounded border-2 p-4 ${s.nichtAnlegbar > 0 ? 'border-amber-300 bg-amber-50/40' : 'border-[#1A1A1A] bg-[#E9FF57]/30'}`}>
        <p className="text-lg font-semibold text-[#1A1A1A]">
          {neuanlage
            ? <>{zahl(s.anlegen)} Artikel würden angelegt — mit {zahl(s.variantenAktiv)} Varianten.</>
            : <>{zahl(s.aktualisieren)} Artikel würden aktualisiert — {zahl(s.variantenAktiv)} Varianten.</>}
        </p>
        {/*
          Zahlen statt Fließtext (Matze, 25.09.2026). Der Satz „179 existieren schon und
          werden übersprungen. 86 hast du abgewählt. 1 keine Kategorie." liest sich
          langsamer als dieselben Zahlen nebeneinander — und was 0 ist, steht gar nicht da.
        */}
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#1A1A1A]/70">
          {neuanlage && s.existiert > 0 && <span>{zahl(s.existiert)} existiert</span>}
          {!neuanlage && s.nichtImBestand > 0 && <span>{zahl(s.nichtImBestand)} nicht in Plenty</span>}
          {s.abgewaehlt > 0 && <span>{zahl(s.abgewaehlt)} abgewählt</span>}
          {neuanlage && s.nichtAnlegbar > 0 && (
            <span className="font-medium text-amber-900">
              {zahl(s.nichtAnlegbar)} nicht anlegbar
            </span>
          )}
          {s.ohneBild > 0 && <span>{zahl(s.ohneBild)} ohne Bild</span>}
          {s.ohneKategorie > 0 && <span>{zahl(s.ohneKategorie)} ohne Kategorie</span>}
        </div>
      </div>

      {ergebnis.warnungen.length > 0 && (
        <ul className="space-y-1 text-sm text-amber-800">
          {ergebnis.warnungen.map((w, i) => <li key={i}>{w}</li>)}
        </ul>
      )}

      {/* Phase 5: die Tabelle je Artikel ist Nachschlagen — die Prüfansicht oben zeigt
          dasselbe schöner. Zugeklappt, damit Schritt 3 nur Zahlen + Knopf zeigt. */}
      <details className="space-y-3 rounded border border-[#DAD9D9] p-3 text-sm">
        <summary className="cursor-pointer font-medium text-[#1A1A1A]">Details je Artikel</summary>
      {/* ── Filter ───────────────────────────────────────────────────────── */}
      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        {([
          ['alle', `Alle ${zahl(plan.artikel.length)}`],
          ['aktiv', `${neuanlage ? 'Anlegen' : 'Aktualisieren'} ${zahl(s.aktiv)}`],
          ['problem', `Braucht Aufmerksamkeit ${zahl(s.nichtAnlegbar + s.ohneBild + s.ohneKategorie)}`],
          ['uebersprungen', `Übersprungen ${zahl(s.existiert + s.nichtImBestand + s.abgewaehlt)}`],
        ] as Array<[Filter, string]>).map(([f, label]) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`rounded border px-2.5 py-1 ${
              filter === f ? 'border-[#1A1A1A] bg-[#E9FF57]' : 'border-[#DAD9D9] bg-white hover:border-[#1A1A1A]'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Ein Artikel je Zeile ─────────────────────────────────────────── */}
      <div className="max-h-[32rem] overflow-y-auto rounded border border-[#DAD9D9]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-[#F7F7F5] text-left text-xs uppercase tracking-wide text-[#1A1A1A]/60">
            <tr>
              <th className="px-3 py-2">Artikel</th>
              <th className="px-3 py-2">Aktion</th>
              <th className="px-3 py-2 text-right">Varianten</th>
              <th className="px-3 py-2 text-right">Bilder</th>
              <th className="px-3 py-2 text-right">Kategorien</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#DAD9D9]">
            {sichtbar.map(a => {
              const k = AKTION[a.aktion];
              const Icon = k.icon;
              const aktiv = a.aktion === 'anlegen' || a.aktion === 'aktualisieren';
              return (
                <tr key={a.schluessel} className={a.aktion === 'abgewaehlt' ? 'opacity-60' : ''}>
                  <td className="px-3 py-2">
                    <span className="font-medium text-[#1A1A1A]">{a.name}</span>
                    {a.grund && (
                      <span className="mt-0.5 block text-xs text-[#1A1A1A]/60">{a.grund}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs ${k.farbe}`}>
                      <Icon className="h-3.5 w-3.5" aria-hidden /> {k.text}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{zahl(a.varianten)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {aktiv && a.ohneBild
                      ? <span className="rounded bg-amber-50 px-1.5 text-amber-900" title="Kein Foto im gewählten Ordner — kann nachgeliefert werden.">ohne</span>
                      : zahl(a.bilder)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {/*
                      🔑 Die Zahl ist ein Knopf. Matze, 22.09.2026: „Wie kann ich in
                      diesem Step eine Kategorie vergeben, wenn automatisch keine
                      gefunden wurde?" — Die Antwort gehört an die Stelle, an der die
                      Frage entsteht, nicht in eine andere Ansicht.
                    */}
                    {entwurfId && aktiv
                      ? (
                        <button
                          type="button"
                          onClick={() => setKategorieFuer(
                            kategorieFuer === a.schluessel ? null : a.schluessel,
                          )}
                          className={`rounded px-1.5 underline-offset-2 hover:underline ${
                            a.ohneKategorie ? 'bg-amber-50 text-amber-900' : 'text-[#1A1A1A]'
                          }`}
                          title={a.ohneKategorie
                            ? 'Keine Kategorie zugeordnet — klicken, um eine zu wählen.'
                            : 'Klicken, um die Kategorie zu ändern.'}
                        >
                          {a.ohneKategorie ? 'ohne' : zahl(a.kategorien)}
                        </button>
                      )
                      : aktiv && a.ohneKategorie
                        ? <span className="rounded bg-amber-50 px-1.5 text-amber-900" title="Keine Plenty-Kategorie zugeordnet — der Artikel entsteht ohne.">ohne</span>
                        : zahl(a.kategorien)}
                  </td>
                </tr>
              );
            })}
            {sichtbar.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-4 text-center text-[#1A1A1A]/60">Kein Artikel in dieser Gruppe.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Die Auswahl steht unter der Tabelle, damit die Zeile sichtbar bleibt. */}
      {entwurfId && kategorieFuer && (() => {
        const artikel = ergebnis.plan.artikel.find(a => a.schluessel === kategorieFuer);
        if (!artikel) return null;
        return (
          <KategorieWahl
            entwurfId={entwurfId}
            artikelSchluessel={artikel.schluessel}
            artikelName={artikel.name}
            dateiwert={artikel.kategorieWert}
            aktuell={[]}
            aufAbbruch={() => setKategorieFuer(null)}
            aufGespeichert={meldung => {
              setKategorieFuer(null);
              aufKategorieGesetzt?.(meldung);
            }}
          />
        );
      })()}

      </details>

      {/* ── Technik, zugeklappt ──────────────────────────────────────────── */}
      <details className="rounded border border-[#DAD9D9] p-3 text-sm">
        <summary className="cursor-pointer font-medium text-[#1A1A1A]">
          Technik · {zahl(aufwand.gesamt)} Schreibvorgänge
        </summary>
        <div className="mt-3 space-y-3 text-[#1A1A1A]/80">
          <p>
            {zahl(aufwand.createCalls)} zum Anlegen · {zahl(aufwand.folgeCalls)} für Eigenschaften
            und Tags (gebatcht aus {zahl(aufwand.relationen)} Relationen und {zahl(aufwand.tagEintraege)} Tags)
            · {zahl(aufwand.aktivierenCalls)} zum Aktivieren.
          </p>
          {ergebnis.beispiele.map((b, i) => (
            <details key={i} className="rounded border border-[#DAD9D9] p-2">
              <summary className="cursor-pointer text-xs font-medium">Payload „{b.artikel}"</summary>
              <div className="mt-2 space-y-2 text-xs">
                <Block titel="POST /items" wert={b.itemPayload} />
                {b.variantenPayloads[0] && <Block titel="POST /items/<id>/variations" wert={b.variantenPayloads[0]} />}
                {b.relationen.length > 0 && <Block titel="POST /properties/relations (Hauptvariante, Id 0 = erst nach dem Anlegen bekannt)" wert={b.relationen} />}
                {b.tagEintraege.length > 0 && <Block titel="PUT /pim/variations/tags" wert={b.tagEintraege} />}
              </div>
            </details>
          ))}
        </div>
      </details>
    </div>
  );
}

function Block({ titel, wert }: { titel: string; wert: unknown }) {
  return (
    <div>
      <p className="mb-1 font-medium text-[#1A1A1A]/70">{titel}</p>
      <pre className="overflow-x-auto rounded bg-[#F7F7F5] p-2">{JSON.stringify(wert, null, 2)}</pre>
    </div>
  );
}
